import { Outlet, useLocation } from "react-router-dom";
import { useEffect } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

export default function Layout() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" in window ? "instant" : "auto" });
  }, [pathname]);

  // Lightweight scroll-reveal observer. Re-runs on route change AND watches
  // for newly mounted [data-reveal] nodes (e.g. async-loaded protected pages
  // like /portal). Fully respects `prefers-reduced-motion` via index.css.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const reduce =
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const revealAll = () =>
      document
        .querySelectorAll("[data-reveal]:not(.revealed)")
        .forEach((el) => el.classList.add("revealed"));

    if (reduce || typeof IntersectionObserver === "undefined") {
      revealAll();
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("revealed");
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: "0px 0px -6% 0px" },
    );

    const observe = (el) => {
      if (el && !el.classList.contains("revealed")) io.observe(el);
    };
    document.querySelectorAll("[data-reveal]:not(.revealed)").forEach(observe);

    // Catch nodes mounted after the initial pass (Suspense boundaries, async
    // protected route content, tab switches, etc.).
    const mo = new MutationObserver((muts) => {
      muts.forEach((m) =>
        m.addedNodes.forEach((n) => {
          if (n.nodeType !== 1) return;
          if (n.matches && n.matches("[data-reveal]")) observe(n);
          if (n.querySelectorAll) {
            n.querySelectorAll("[data-reveal]:not(.revealed)").forEach(observe);
          }
        }),
      );
    });
    mo.observe(document.body, { childList: true, subtree: true });

    return () => {
      io.disconnect();
      mo.disconnect();
    };
  }, [pathname]);

  return (
    <div
      className="min-h-screen flex flex-col bg-white"
      data-testid="site-layout"
    >
      <Navbar />
      <main className="flex-1 pt-20" data-testid="site-main">
        <Outlet />
      </main>
      <Footer />
    </div>
  );
}
